import { Account } from '@/domain/usecases'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { UserOnboarding } from '@/domain/usecases/onboarding/user-onboarding'
import { UserProfileAssessment } from '@/domain/usecases/profile-assessment/user-profile-assessment'
import { queryClient } from '@/infra/http'
import { makeSkillWebContentPageRedirectUrl } from '@/main/factories/http'
import {
  importRemote,
  contentDetailsPromise,
  enrollmentContentInfoPromise
} from '@/main/routes/helpers/loaders'
import { redirect } from 'react-router-dom'

/**
 * Loads content enrollment data
 * Complex loader that fetches account, onboarding, content details and profile assessment
 * Redirects if content is restricted
 */
export async function loadContentEnrollment(id: number) {
  // Check if content is accessible
  const userEnrollmentContentInfo = await enrollmentContentInfoPromise(queryClient, id, true)
  if (!userEnrollmentContentInfo || userEnrollmentContentInfo.restricted) {
    return redirect(makeSkillWebContentPageRedirectUrl(id))
  }

  const [
    remoteAccountProfile,
    onboardingOptions,
    userContentDetails,
    [userOnboarding, userProfileAssessment]
  ] = await Promise.all([
    importRemote<Account>('makeRemoteAccountProfile', queryClient),
    importRemote<OnboardingOptions>('makeRemoteOnboardingOptions', queryClient).then(remote =>
      remote.getStep4()
    ),
    contentDetailsPromise(queryClient, id, true),
    Promise.allSettled([getUserOnboarding(), getUserProfileAssessment()]).then(items =>
      items.map(item => (item.status === 'fulfilled' ? item.value : null))
    )
  ])

  // Double check restriction after all promises resolve
  if (userEnrollmentContentInfo.restricted) {
    return redirect(makeSkillWebContentPageRedirectUrl(id))
  }

  return {
    remoteAccountProfile,
    accountProfile: await remoteAccountProfile.loader(),
    onboardingOptions,
    userContentDetails,
    userEnrollmentContentInfo,
    userSkills: (userOnboarding as UserOnboarding.Model | null)?.interest?.skills || [],
    userProfileAssessment: userProfileAssessment as UserProfileAssessment.Model | null
  }
}

async function getUserOnboarding() {
  const remote = await importRemote<UserOnboarding>('makeRemoteUserOnboarding', queryClient)
  return remote.get()
}

async function getUserProfileAssessment() {
  const remote = await importRemote<UserProfileAssessment>(
    'makeRemoteUserProfileAssessment',
    queryClient
  )
  return remote.getLatest()
}
